import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, FormState, Multistep, useFormApi } from 'informed';
import {
  ArchivingModal,
  AssetStep,
  DetailsStep,
  ReviewStep,
  RulesStep,
  SideMenu,
  TimeStep,
  UsersStep,
} from './components';
import { useRepository } from '@context/repository.context';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IAdminFacilitiesOptions, IAdminFacilityDetails, Nullable } from '@common/interfaces';
import { Button, Main } from '@components';
import { FormValues, IYupSchema, yupSchemas, STEPS, FormTempValues } from './consts';
import { DEFAULT_SERVER_TIME_FORMAT, URLS } from '@common/constants';
import { parse } from 'date-fns';
import { toast } from 'react-toastify';
import { mapServices, mapToWorkSchedule, toWeekdays } from './utils';
import css from './styles.module.scss';
import { ArrowLeftIcon } from '@assets/svg';

export const convertValuesToSubmit = (values: FormValues) => {
  const {
    asset: { services },
    details: { address, city_name, country, depot_number, email, image, is_active, latitude, longitude, name },
    rules: { global, agencies },
    time,
    users,
    review,
  } = values;

  const formData = new FormData(); // eq. interface FacilityFormData
  const file = image?.[0];

  formData.append('name', name);
  formData.append('is_active', String(is_active));
  formData.append('email', email);
  formData.append('address', address);
  formData.append('latitude', latitude);
  formData.append('longitude', longitude);
  formData.append('country_iso_code', country?.value ?? '');
  formData.append('city_name', city_name?.value ?? '');
  formData.append('depot_number', depot_number?.value ?? '');
  if (file instanceof File) {
    formData.append('image', file);
  } else if (!file) {
    formData.append('image', '');
  }
  formData.append('global_visibility_rules', global ? String(parseFloat(global.value)) : '');
  formData.append('allowed_organisations', JSON.stringify(agencies.map(({ value }) => parseFloat(value))));
  formData.append('work_time', JSON.stringify(mapToWorkSchedule(time)));
  formData.append(
    'services',
    JSON.stringify(
      services.map(({ type_of_asset, type_of_repairs }) => ({
        type_of_asset: type_of_asset?.value ?? null,
        type_of_repairs: type_of_repairs.map(({ value }) => value),
      }))
    )
  );
  formData.append(
    'focal_points',
    JSON.stringify(users?.focal_points.filter((user) => user !== null).map((user) => user!.label))
  );
  if (review?.notify_users) {
    formData.append('notify_users', JSON.stringify(review.notify_users));
  }

  return formData;
};

const convertFacilityToFormState = (
  isNew: boolean,
  facility?: IAdminFacilityDetails,
  formOptions?: IAdminFacilitiesOptions
) => {
  if (!formOptions) return null;

  if (isNew)
    return {
      details: {
        address: '',
        city_name: null,
        country: null,
        depot_number: null,
        email: '',
        image: [],
        is_active: true,
        latitude: '',
        longitude: '',
        name: '',
        status: null,
      },
      rules: {
        global: null,
        agencies: [],
      },
      time: {
        start_time: null,
        end_time: null,
        weekdays: [],
      },
      asset: {
        services: [
          {
            type_of_asset: null,
            type_of_repairs: [],
          },
        ],
      },
      users: {
        focal_points: [null],
      },
      review: {
        notify_users: [],
      },
    };

  if (!facility) return null;

  const {
    address,
    allowed_organisations,
    city_name,
    country,
    depot,
    email,
    focal_points,
    global_visibility_rules,
    image,
    is_active,
    latitude,
    longitude,
    name,
    services,
    work_time,
  } = facility;

  const data = {
    details: {
      address,
      city_name: { label: city_name, value: city_name },
      country: country ? { label: country.name, value: country.iso_code } : null,
      depot_number: depot,
      email,
      is_active,
      latitude: String(latitude ?? ''),
      longitude: String(longitude ?? ''),
      name: name,
      status: null,
      image: image
        ? [
            {
              url: image,
              name: image.split('/').pop() ?? 'image',
              size: 0,
              type: 'image/*',
              __existing: true,
            },
          ]
        : [],
    },
    rules: {
      global:
        formOptions?.global_visibility_rules.find(({ value }) => value === String(global_visibility_rules)) || null,
      agencies: allowed_organisations.map(({ short_name, id_on_unbh }) => ({
        label: short_name,
        value: String(id_on_unbh),
      })),
    },
    time: {
      start_time: parse(work_time.start_time, DEFAULT_SERVER_TIME_FORMAT, new Date()),
      end_time: parse(work_time.end_time, DEFAULT_SERVER_TIME_FORMAT, new Date()),
      weekdays: toWeekdays(work_time),
    },
    asset: {
      services: mapServices(services),
    },
    users: {
      focal_points: focal_points.map(({ email }) => ({ label: email, value: email })),
    },
  };

  return data;
};

// Note: Force initial data for Form State in editing process
const InitEditableData = ({ isNew, initialValues }: { isNew: boolean; initialValues: any }) => {
  const formApi = useFormApi();
  useEffect(() => {
    if (!isNew && initialValues) {
      Object.keys(initialValues).forEach((stepKey) => {
        Object.keys(initialValues[stepKey]).forEach((fieldKey) => {
          formApi.setValue(`${stepKey}.${fieldKey}`, initialValues[stepKey][fieldKey]);
        });
      });
    }
  }, [initialValues, isNew]);
  return null;
};

const AdminAreaFacilityFormPage: FC = () => {
  const navigate = useNavigate();
  const { id: facilityId = '' } = useParams<{ id: string }>();
  const { facilityRepository } = useRepository();
  const queryClient = useQueryClient();

  const [yupSchema, setYupSchema] = useState<IYupSchema>(yupSchemas[STEPS.DETAILS]);
  const [, setKey] = useState(1);
  const [archivingModalShow, setArchivingModalShow] = useState(false);
  const [sendingData, setSendingData] = useState<Nullable<FormData>>(null);

  const isNew = facilityId === 'new';

  const { data: formOptions, isLoading: isOptionsLoading } = useQuery<IAdminFacilitiesOptions>('form-options', () =>
    facilityRepository.getAdminFacilitiesOptions()
  );

  const { data: facility, isLoading: isFacilityLoading } = useQuery<IAdminFacilityDetails>(
    ['facility', facilityId],
    () => facilityRepository.getAdminFacility(facilityId),
    { enabled: !isNew }
  );

  const { mutate: createAdminFacility, isLoading: creatingInProgress } = useMutation(
    (data: FormData) => facilityRepository.createAdminFacility(data),
    {
      onSuccess: () => {
        toast.success('Facility was created successfully');
        navigate(URLS.ADMIN_AREA_FACILITIES);
      },
    }
  );

  const { mutate: patchAdminFacility, isLoading: patchingInProgress } = useMutation(
    (data: FormData) => facilityRepository.patchAdminFacility(facilityId, data),
    {
      onSuccess: () => {
        toast.success('Facility was updated successfully');
        queryClient.invalidateQueries(['facility', facilityId]);
        navigate(URLS.ADMIN_AREA_FACILITIES);
      },
    }
  );

  const sendRequest = (data: FormData) => {
    if (isNew) {
      createAdminFacility(data);
    } else {
      patchAdminFacility(data);
    }
  };

  const submitRequest = (archive_notification: boolean) => {
    if (sendingData) {
      sendingData.append('archive_notification', String(archive_notification));
      sendRequest(sendingData);
      setSendingData(null);
    }
  };

  const onFormSubmit = ({ values }: FormState) => {
    const data = values as unknown as FormValues;
    const finalValues = convertValuesToSubmit(data);
    const archived = !isNew && !data.details.is_active && facility?.is_active;
    if (archived) {
      setArchivingModalShow(true);
      setSendingData(finalValues);
    } else {
      sendRequest(finalValues);
    }
  };

  const onFormChange = () => setKey((prevKey) => prevKey + 1);

  const loading = isOptionsLoading || isFacilityLoading || creatingInProgress || patchingInProgress;

  const initialValues = useMemo<Nullable<FormTempValues>>(
    () => convertFacilityToFormState(isNew, facility, formOptions),
    [facility, formOptions]
  );

  return (
    <Main loading={loading} background='admin'>
      {initialValues && formOptions ? (
        <Form
          onChange={onFormChange}
          onSubmit={onFormSubmit}
          yupSchema={yupSchema}
          initialValues={initialValues as Record<string, unknown>}
        >
          <InitEditableData isNew={isNew} initialValues={initialValues} />
          <Multistep>
            <div className={css.container}>
              <div className={css.sidebar}>
                <Button
                  text='Back to facilities'
                  variant='text'
                  iconL={<ArrowLeftIcon />}
                  link={URLS.ADMIN_AREA_FACILITIES}
                />
                <SideMenu isNew={isNew} setYupSchema={setYupSchema} initialValues={initialValues} />
              </div>
              <div className={css.content}>
                <DetailsStep
                  setYupSchema={setYupSchema}
                  countriesOptions={formOptions.countries}
                  depotOptions={formOptions.depots}
                />
                <RulesStep
                  setYupSchema={setYupSchema}
                  rulesOptions={formOptions.global_visibility_rules}
                  agenciesOptions={formOptions.organisations}
                />
                <TimeStep setYupSchema={setYupSchema} />
                <AssetStep
                  setYupSchema={setYupSchema}
                  assetsOptions={formOptions.type_of_assets}
                  repairsOptions={formOptions.type_of_repairs}
                />
                <UsersStep setYupSchema={setYupSchema} usersOptions={formOptions.users} />
                <ReviewStep isNew={isNew} setYupSchema={setYupSchema} />
              </div>
            </div>
          </Multistep>
        </Form>
      ) : null}
      {archivingModalShow ? (
        <ArchivingModal
          facilityId={facilityId}
          submitRequest={submitRequest}
          closeModal={() => setArchivingModalShow(false)}
        />
      ) : null}
    </Main>
  );
};

export default AdminAreaFacilityFormPage;
